import { createFileRoute } from '@tanstack/react-router'
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { useI18n } from '@/hooks/useI18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    LinkIcon,
    UsersIcon,
    ClockIcon,
    ShoppingCartIcon,
    CloudIcon
} from '@heroicons/react/24/outline';

interface Integration {
    id: string;
    name: string;
    description: string;
    icon: React.ComponentType<any>;
    iconBgColor: string;
    iconColor: string;
    href: string;
    status?: 'active' | 'inactive' | 'pending';
}

export default function IntegrationsPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const { t } = useI18n();

    const integrations: Integration[] = [
        {
            id: 'connecteam',
            name: 'Connecteam',
            description: 'Employee management, time tracking, and scheduling',
            icon: UsersIcon,
            iconBgColor: 'bg-blue-100',
            iconColor: 'text-blue-600',
            href: '/admin/integrations/connecteam',
            status: 'active'
        },
        {
            id: 'fruit-bi',
            name: 'Fruit BI',
            description: 'POS integration for menu items and sales data',
            icon: ShoppingCartIcon,
            iconBgColor: 'bg-green-100',
            iconColor: 'text-green-600',
            href: '/sales/integrations',
            status: 'active'
        },
       /* {
            id: 'nav',
            name: 'NAV (Hungarian Tax Office)',
            description: 'Invoice reporting and tax compliance',
            icon: CloudIcon,
            iconBgColor: 'bg-purple-100',
            iconColor: 'text-purple-600',
            href: '/admin/integrations/nav',
            status: 'active'
        },
        {
            id: 'google-drive',
            name: 'Google Drive',
            description: 'Invoice document storage and retrieval',
            icon: CloudIcon,
            iconBgColor: 'bg-red-100',
            iconColor: 'text-red-600',
            href: '/admin/integrations/google-drive',
            status: 'active'
        }*/
    ];

    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusConfig = {
            active: { label: 'Active', className: 'bg-green-100 text-green-800' },
            inactive: { label: 'Inactive', className: 'bg-gray-100 text-gray-800' },
            pending: { label: 'Pending Setup', className: 'bg-yellow-100 text-yellow-800' }
        };

        const config = statusConfig[status as keyof typeof statusConfig];
        if (!config) return null;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
                {config.label}
            </span>
        );
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                        <LinkIcon className="h-8 w-8" />
                        Integrations
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage external service integrations and data synchronization
                    </p>
                </div>

                {/* Integrations Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {integrations.map((integration) => {
                        const IconComponent = integration.icon;
                        return (
                            <Card
                                key={integration.id}
                                className="hover:shadow-lg transition-shadow cursor-pointer"
                                onClick={() => navigate({ to: integration.href})}
                            >
                                <CardHeader>
                                    <div className="flex items-start justify-between">
                                        <div className={`p-3 rounded-lg ${integration.iconBgColor}`}>
                                            <IconComponent className={`h-6 w-6 ${integration.iconColor}`} />
                                        </div>
                                        {getStatusBadge(integration.status)}
                                    </div>
                                    <CardTitle className="mt-4">{integration.name}</CardTitle>
                                    <CardDescription>{integration.description}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Button
                                        variant="outline"
                                        className="w-full"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate({ to: integration.href});
                                        }}
                                    >
                                        Configure
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>


            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/admin/integrations\/')({
  component: IntegrationsPage,
})
